/* eslint-disable */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { createNewPlan } from "store/features/plans/plansActions";
import addSvg from "assets/modals/add.svg";

const AddPlansModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    billing_cycle: "monthly",
    max_users: "",
    max_employees: "",
    max_storage_mb: "",
    trial_days: "14",
    is_active: true,
    is_trial_allowed: true,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await dispatch(createNewPlan(formData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      billing_cycle: "monthly",
      max_users: "",
      max_employees: "",
      max_storage_mb: "",
      trial_days: "14",
      is_active: true,
      is_trial_allowed: true,
    });
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "primary.main",
      },
      "& .MuiInputLabel-asterisk": {
        color: "#ef4444",
      },
    },
    "& .MuiInputBase-input": {
      color: "text.primary",
    },
    "& .MuiInputBase-inputMultiline": {
      color: "text.primary",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal with slide-in from right animation */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header*/}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                flexShrink: 0,
                position: "relative",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                {" "}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addSvg}
                      alt="Add Plan"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Create New Plan
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Add a new pricing plan for your customers
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Close Button  */}
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Scrollable Form Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#f59e0b",
                  borderRadius: "3px",
                },
              }}
            >
              <form onSubmit={handleSubmit}>
                <Box sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    {/* Plan Name */}
                    <TextField
                      fullWidth
                      label="Plan Name "
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Starter Pro, Business Plus"
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* Description */}
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      placeholder="Brief description of this plan"
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    {/* Price and Billing Cycle */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Price (₹)"
                        name="price"
                        type="number"
                        value={formData.price}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                        inputProps={{ step: "0.01" }}
                        sx={inputSx}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Billing Cycle"
                        name="billing_cycle"
                        value={formData.billing_cycle}
                        onChange={handleChange}
                        required
                        sx={inputSx}
                        SelectProps={{
                          MenuProps: {
                            disablePortal: true,
                            sx: { zIndex: 9999 },
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                      </TextField>
                    </Stack>

                    {/* Limits */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Max Users"
                        name="max_users"
                        type="number"
                        value={formData.max_users}
                        onChange={handleChange}
                        placeholder="Unlimited"
                        sx={inputSx}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Max Employees"
                        name="max_employees"
                        type="number"
                        value={formData.max_employees}
                        onChange={handleChange}
                        placeholder="Unlimited"
                        sx={inputSx}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Storage (MB)"
                        name="max_storage_mb"
                        type="number"
                        value={formData.max_storage_mb}
                        onChange={handleChange}
                        placeholder="Unlimited"
                        sx={inputSx}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Stack>

                    {/* Trial Period */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ sm: "center" }}
                    >
                      <TextField
                        fullWidth
                        label="Trial Days"
                        name="trial_days"
                        type="number"
                        value={formData.trial_days}
                        onChange={handleChange}
                        placeholder="14"
                        sx={inputSx}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="is_trial_allowed"
                              checked={formData.is_trial_allowed}
                              onChange={handleChange}
                              sx={{
                                color: "#d1d5db",
                                "&.Mui-checked": {
                                  color: "primary.main",
                                },
                              }}
                            />
                          }
                          label="Allow Trial"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="is_active"
                              checked={formData.is_active}
                              onChange={handleChange}
                              sx={{
                                color: "#d1d5db",
                                "&.Mui-checked": {
                                  color: "primary.main",
                                },
                              }}
                            />
                          }
                          label="Active"
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>

                {/* Buttons - fixed at bottom */}
                <Box
                  sx={{
                    p: 3,
                    borderTop: `1px solid ${
                      theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                    }`,
                    display: "flex",
                    gap: 2,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                    flexShrink: 0,
                  }}
                >
                  <Button
                    onClick={onClose}
                    disabled={loading}
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor:
                        theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                      color:
                        theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                      "&:hover": {
                        borderColor: "#f59e0b",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(245,158,11,0.1)"
                            : "#fffbeb",
                      },
                    }}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 500,
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#ffffff",
                      boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                        transform: "scale(1.02)",
                        boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                      },
                      "&:disabled": {
                        opacity: 0.7,
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                    ) : (
                      "Create Plan"
                    )}
                  </Button>
                </Box>
              </form>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddPlansModal;
